import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, XCircle, AlertTriangle, Lightbulb, FileText, BookOpen, Pen } from 'lucide-react';
import { Button } from '../components/ui/button';
import { MarketingNav, MarketingFooter } from '../components/MarketingLayout';
import { SEO } from '../components/SEO';

const commonMistakes = [
  "Opening with a quote or dictionary definition",
  "Writing about a trip abroad as a 'life-changing experience' without specificity",
  "Listing accomplishments instead of telling a story",
  "Using the essay to repeat what's already in the activities section",
  "Being generic — writing an essay that could belong to anyone",
  "Trying to sound impressive instead of authentic",
  "Ignoring the word limit (too short signals laziness; too long signals poor editing)",
  "Not answering the actual prompt",
];

const whatOfficersLookFor = [
  { title: "Authentic Voice", desc: "Officers read 30-50 apps a day. They can detect a ghostwritten or AI-generated essay in seconds. They want to hear you — your syntax, your humor, your observations." },
  { title: "Specificity Over Generality", desc: "Don't say 'I learned the value of hard work.' Say 'I spent 47 nights rewriting the third act of a play nobody asked me to write.' Details are proof." },
  { title: "Self-Awareness", desc: "The best essays show a student who has reflected honestly on who they are — not just what they've done. Growth, contradiction, nuance." },
  { title: "Structure & Craft", desc: "An essay with a clear arc — setup, tension, resolution — reads as mature and intentional. Admissions officers notice when the writing itself demonstrates skill." },
  { title: "Institutional Fit", desc: "For supplemental essays: demonstrate that you've researched the school deeply. Name specific programs, professors, traditions, courses. Generic 'Why Us' essays are instant rejects." },
];

const essayTypes = [
  {
    title: "The Personal Statement (Common App)",
    prompts: [
      "Some students have a background, identity, interest, or talent that is so meaningful they believe their application would be incomplete without it.",
      "The lessons we take from obstacles we encounter can be fundamental to later success.",
      "Reflect on a time when you questioned or challenged a belief or idea.",
      "Reflect on something that someone has done for you that has made you happy or thankful.",
      "Discuss an accomplishment, event, or realization that sparked personal growth.",
      "Describe a topic, idea, or concept you find so engaging that it makes you lose all track of time.",
      "Share an essay on any topic of your choice.",
    ],
    tip: "The best prompt is the one that gives you the most authentic story. Don't pick the 'hardest' prompt — pick the one you can be most specific about.",
    wordLimit: "650 words max",
  },
  {
    title: "Why This School (Supplemental)",
    tips: [
      "Name 2-3 specific programs, courses, or professors",
      "Reference campus traditions or unique opportunities",
      "Connect your interests to what the school uniquely offers",
      "Avoid anything that could apply to any other school",
    ],
    wordLimit: "150-400 words typically",
  },
  {
    title: "Activity / Extracurricular Essay",
    tips: [
      "Go deep on ONE activity, not broad across many",
      "Focus on what you learned, not just what you did",
      "Show how this activity reveals your values",
      "Use concrete numbers and outcomes",
    ],
    wordLimit: "150-300 words typically",
  },
  {
    title: "Community / Identity Essay",
    tips: [
      "Be specific about which community and your role in it",
      "Show both what the community gave you and what you gave back",
      "Avoid stereotypes or surface-level cultural references",
      "Connect community to personal growth",
    ],
    wordLimit: "200-400 words typically",
  },
];

export default function EssayGuidePage() {
  return (
    <div className="min-h-screen bg-white" data-testid="essay-guide-page">
      <SEO title="College Essay Guide — What Admissions Officers Actually Look For" description="The definitive guide to writing college application essays. What admissions officers look for, common mistakes to avoid, prompt breakdowns for Common App and supplements, and writing strategies that actually work." />
      <MarketingNav />

      <section className="pt-16 bg-[#0F392B] py-20 md:py-28">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm font-medium text-[#D4AF37] tracking-wide uppercase mb-4">The Definitive Resource</p>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            The College Essay Guide
          </h1>
          <p className="text-base text-white/70 max-w-xl mx-auto">
            What admissions officers actually look for — written by people who used to be admissions officers.
          </p>
        </div>
      </section>

      {/* What Officers Look For */}
      <section className="py-16 md:py-24" data-testid="officers-section">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>What Admissions Officers Actually Look For</h2>
          <p className="text-sm text-muted-foreground mb-10">From people who've read thousands of application essays in committee rooms.</p>
          <div className="space-y-5">
            {whatOfficersLookFor.map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-5 rounded-xl bg-[#F9F9F7] border border-[#E2E2E2]" data-testid={`officer-tip-${i}`}>
                <div className="w-8 h-8 rounded-lg bg-[#0F392B] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-semibold text-white">{i + 1}</span>
                </div>
                <div>
                  <h3 className="text-base font-medium mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-16 bg-[#F9F9F7] border-y border-[#E2E2E2]" data-testid="mistakes-section">
        <div className="max-w-4xl mx-auto px-4">
          <div className="flex items-center gap-3 mb-8">
            <AlertTriangle className="h-6 w-6 text-red-500" />
            <h2 className="text-2xl font-medium" style={{ fontFamily: "'Playfair Display', serif" }}>8 Mistakes That Kill Applications</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {commonMistakes.map((m, i) => (
              <div key={i} className="flex items-start gap-3 p-4 rounded-lg bg-white border border-[#E2E2E2]" data-testid={`mistake-${i}`}>
                <XCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm">{m}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Essay Types & Prompts */}
      <section className="py-16 md:py-24" data-testid="prompts-section">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-2xl md:text-3xl font-medium tracking-tight mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>Essay Types & Prompt Breakdowns</h2>
          <p className="text-sm text-muted-foreground mb-10">Every essay type you'll encounter, with strategy for each.</p>
          <div className="space-y-8">
            {essayTypes.map((type, i) => (
              <div key={i} className="rounded-xl border border-[#E2E2E2] overflow-hidden" data-testid={`essay-type-${i}`}>
                <div className="bg-[#0F392B] px-6 py-4 flex items-center justify-between">
                  <h3 className="text-base font-medium text-white" style={{ fontFamily: "'Playfair Display', serif" }}>{type.title}</h3>
                  <span className="text-xs text-[#D4AF37]">{type.wordLimit}</span>
                </div>
                <div className="p-6">
                  {type.prompts && (
                    <div className="mb-4">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">2025-2026 Prompts</p>
                      <ol className="space-y-2">
                        {type.prompts.map((p, j) => (
                          <li key={j} className="flex items-start gap-2 text-sm">
                            <span className="text-xs font-medium text-[#0F392B] mt-0.5 w-4 flex-shrink-0">{j + 1}.</span>
                            <span className="text-muted-foreground">{p}</span>
                          </li>
                        ))}
                      </ol>
                    </div>
                  )}
                  {type.tip && (
                    <div className="flex items-start gap-2 p-3 rounded-lg bg-[#D4AF37]/5 border border-[#D4AF37]/10">
                      <Lightbulb className="h-4 w-4 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                      <p className="text-sm italic">{type.tip}</p>
                    </div>
                  )}
                  {type.tips && (
                    <div className="space-y-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">Strategy</p>
                      {type.tips.map((t, j) => (
                        <div key={j} className="flex items-start gap-2">
                          <CheckCircle className="h-3.5 w-3.5 text-[#0F392B] mt-0.5 flex-shrink-0" />
                          <span className="text-sm">{t}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portal CTA */}
      <section className="py-16 bg-[#0F392B]">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <Pen className="h-8 w-8 text-[#D4AF37] mx-auto mb-4" />
          <h3 className="text-2xl font-medium text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Ready to start writing?</h3>
          <p className="text-sm text-white/60 mb-6">The portal's Essay Studio lets you draft, revise, and get AI feedback on every essay — from brainstorming to final polish. Plus a 45-minute session with a former admissions officer.</p>
          <Link to="/sign-up">
            <Button className="bg-white text-[#0F392B] hover:bg-white/90 h-12 px-8 text-base" data-testid="essay-guide-cta">Access the Portal — $99 <ArrowRight className="ml-2 h-4 w-4" /></Button>
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
